import { useState, useEffect, useCallback } from 'react';

/**
 * Web-Push-Notifications — Subscribe / Unsubscribe / Test.
 *
 * Browser-Support:
 * - Chrome/Edge/Firefox/Safari (macOS) → out of the box
 * - iOS Safari → braucht „Add to Home Screen" (PWA-Standalone)
 *
 * Returns: { state, supported, permission, subscribe, unsubscribe, test }
 *   state:  'idle' | 'subscribing' | 'subscribed' | 'denied' | 'error' | 'unsupported'
 */

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const b64     = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = atob(b64);
  const out     = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export default function usePushNotification(api) {
  const supported   = typeof window !== 'undefined' &&
                      'serviceWorker' in navigator &&
                      'PushManager' in window &&
                      'Notification' in window;

  const [state,      setState]      = useState(supported ? 'idle' : 'unsupported');
  const [permission, setPermission] = useState(supported ? Notification.permission : 'denied');
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Beim Mount: SW registrieren, prüfen ob schon abonniert
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled && sub) {
          setIsSubscribed(true);
          setState('subscribed');
        }
      } catch (e) {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) return false;
    setState('subscribing');
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') { setState('denied'); return false; }

      const { public_key, configured } = await api.get('/api/push/vapid-public');
      if (!configured) { setState('error'); return false; }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
      await api.post('/api/push/subscribe', sub.toJSON());
      setIsSubscribed(true);
      setState('subscribed');
      return true;
    } catch (e) {
      setState('error');
      return false;
    }
  }, [api, supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete('/api/push/subscribe', { endpoint: sub.endpoint }).catch(() => null);
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
      setState('idle');
    } catch (_) { /* ignore */ }
  }, [api, supported]);

  const test = useCallback(async () => {
    try { await api.post('/api/push/test', {}); } catch (_) { /* ignore */ }
  }, [api]);

  return { state, supported, permission, isSubscribed, subscribe, unsubscribe, test };
}
